'use client';

import { Search, Sparkles, Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from '~/components/Divider';
import { Notification } from '~/components/Notification';
import { Toolbar } from '~/components/Toolbar';
import { usePathname, useRouter } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { getEnglishPlaceName, usePlaygroundActions, usePlaygroundContext, usePlaygroundContextParams, usePlaygroundDestinations, usePlaygroundEnglishPlaceNameByPlaceId, usePlaygroundSearchValue, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { PlaygroundPlace } from '~/types/Playground.types';
import { logError, trackEvent } from '~/utils';
import { addSearchToHistory } from '~/utils/aiTravelPlannerHelpers';
import { AnimatedPlaceholder } from '../AnimatedPlaceholder';
import { EditTripButton } from '../Buttons/EditTripButton';
import { useEmailCaptureGate } from '../EmailCaptureForm/useEmailCaptureGate';
import { PlaygroundGenerateTripsButton } from '../PlaygroundGenerateTripsButton/PlaygroundGenerateTripsButton';
import { DatesButton, DatesButtonMobile } from './DatesButton';
import { DestinationsButton, DestinationsButtonMobile } from './DestinationsButton';
import { matchSelectablePlace } from './matchSelectablePlace';
import { OriginButton, OriginButtonMobile } from './OriginButton';
import styles from './PrimaryEditBar.module.css';
interface QuickPinDeps {
  query: string;
  selectablePlaces: ReadonlyArray<PlaygroundPlace>;
  nonSelectablePlaces: ReadonlyArray<PlaygroundPlace>;
  playgroundContext: ReturnType<typeof usePlaygroundContext>;
  addDestinationMutation: ReturnType<typeof api.playground.addDestination.useMutation>;
  setContext: ReturnType<typeof usePlaygroundActions>['setContext'];
  addDestination: ReturnType<typeof usePlaygroundActions>['addDestination'];
  setLastAddedDestination: ReturnType<typeof usePlaygroundActions>['setLastAddedDestination'];
}

// Returns true when a recognised place was pinned and the caller should
// short-circuit; false when the caller should fall through to the AI flow.
async function tryQuickPin({
  query,
  selectablePlaces,
  nonSelectablePlaces,
  playgroundContext,
  addDestinationMutation,
  setContext,
  addDestination,
  setLastAddedDestination
}: QuickPinDeps): Promise<boolean> {
  const match = matchSelectablePlace(query, selectablePlaces, nonSelectablePlaces);
  if (match.kind !== 'single') {
    return false;
  }
  try {
    const res = await addDestinationMutation.mutateAsync({
      playgroundInputContext: playgroundContext,
      placeId: match.placeId
    });
    if (res) {
      setContext(res);
      addDestination(match.placeId);
      setLastAddedDestination(match.placeId);
      trackEvent('destination_pinned', {
        event_category: 'Playground',
        event_label: match.placeName,
        destination_id: match.placeId,
        source: 'search'
      });
      return true;
    }
  } catch (error) {
    logError('Search-bar quick pin failed; falling through to AI', error);
  }
  return false;
}
function PrimaryEditBar() {
  const t = useTranslations();
  const pathname = usePathname();
  const preferences = usePlaygroundContextParams();
  const playgroundContext = usePlaygroundContext();
  const {
    cmsDestinations,
    dpDestinations
  } = usePlaygroundDestinations();
  const unselectablePlaces = cmsDestinations.unselectable.cmsPlaces;
  const availablePlacesInMaps = cmsDestinations.available.cmsPlaces;
  const englishPlaceNameByPlaceId = usePlaygroundEnglishPlaceNameByPlaceId();
  const selectedDestinationIds = usePlaygroundSelectedDestinations().dpIds;
  const searchValue = usePlaygroundSearchValue();
  const [isSearching, setIsSearching] = useState(false);
  const [_searchError, setSearchError] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [_searchResults, setSearchResults] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const logAISuggestion = api.playground.logAISuggestion.useMutation();
  const aiTravelPlanner = api.playground.aiTravelPlanner.useMutation();
  const addDestinationCall = api.playground.addDestination.useMutation();
  const {
    setShowAnimLoader,
    setIsLoading,
    setLoadingMessages,
    setAiTravelPlannerResponse,
    setSearchValue,
    openTripDrawerWithFocus,
    setContext,
    addDestination,
    setLastAddedDestination
  } = usePlaygroundActions();
  const {
    originId,
    startDate,
    endDate,
    endPlaceId
  } = usePlaygroundContextParams();
  const router = useRouter();
  const {
    topRightCtaOpensCapture
  } = useEmailCaptureGate();
  const handleReviewCtaBeforeNavigate = useCallback((): 'block' | 'continue' => {
    openTripDrawerWithFocus('email');
    return 'block';
  }, [openTripDrawerWithFocus]);
  const {
    placeDialog
  } = usePlaygroundUIState();
  const isReview = pathname?.includes('/review');
  let BUTTON_VARIANT;
  if (process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER === 'true') {
    BUTTON_VARIANT = 'pill';
  } else {
    BUTTON_VARIANT = 'tertiary';
  }
  const [showNoResultsNotification, setShowNoResultsNotification] = useState(false);
  const clearSearchHandler = () => {
    setSearchValue('');
    setSearchError(null);
    setSearchComplete(false);
    setAiTravelPlannerResponse(null);
    setShowNoResultsNotification(false);
    setSearchResults('');
  };
  const handleClearButtonMouseDown = (e: React.MouseEvent) => {
    // Prevent the input from losing focus when clicking the clear button
    e.preventDefault();
  };
  const handleSearchButtonMouseDown = (e_0: React.MouseEvent) => {
    // Prevent the input from losing focus when clicking the search button on Safari
    e_0.preventDefault();
  };
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };
  const handleSearchBlur = (e_1: React.FocusEvent) => {
    // Store references before setTimeout to avoid React event pooling issues
    const {
      currentTarget,
      relatedTarget
    } = e_1;

    // Use setTimeout to allow Safari to properly handle focus events
    setTimeout(() => {
      // Check if the new focus target is within the search container
      const searchContainer = currentTarget.closest(`.${styles.textFieldContainer}`);

      // Check if a place dialog is open - if so, don't collapse the search bar
      if (placeDialog) {
        return;
      }
      if (!searchContainer?.contains(relatedTarget)) {
        setIsSearchFocused(false);
      }
    }, 0);
  };
  const handleContainerClick = () => {
    if (isReview) {
      // Navigate back to build from review
      if (!preferences?.originId) return;
      setIsLoading(true);
      if (window.history.length > 1) {
        router.back();
      } else {
        const url = new URL('/map/build', window.location.origin);
        url.searchParams.set('origin', originId.toString());
        if (endPlaceId) {
          url.searchParams.set('endPlace', endPlaceId.toString());
        }
        if (startDate && endDate) {
          url.searchParams.set('startDate', startDate);
          url.searchParams.set('endDate', endDate);
        }
        router.replace(url.pathname + url.search);
      }
      return;
    }
    setIsSearchFocused(true);
  };
  const handleSearchSubmit = async () => {
    setIsSearchFocused(true);

    // Focus the input field to maintain expanded state
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    trackEvent('playground_ai_search_click', {
      event_category: 'Playground',
      event_label: 'playground_ai_search_click',
      search_value: searchValue
    });
    if (!searchValue.trim() || searchValue.length < 3) {
      setSearchError(null);
      return;
    }
    addSearchToHistory(searchValue);
    setShowNoResultsNotification(false);
    setAiTravelPlannerResponse(null);
    const isQuickPinEnabled = process.env.NEXT_PUBLIC_FF_ENABLE_SEARCH_BAR_QUICK_PIN === 'true';
    if (isQuickPinEnabled) {
      const selectableForMatch = dpDestinations.selectable.places.filter(place => !selectedDestinationIds.includes(place.id));
      const nonSelectableForMatch = [...dpDestinations.unselectable.places, ...dpDestinations.unsupported.places];
      const pinned = await tryQuickPin({
        query: searchValue,
        selectablePlaces: selectableForMatch,
        nonSelectablePlaces: nonSelectableForMatch,
        playgroundContext,
        addDestinationMutation: addDestinationCall,
        setContext,
        addDestination,
        setLastAddedDestination
      });
      if (pinned) {
        return;
      }
    }
    setSearchComplete(false);
    setIsSearching(true);
    setLoadingMessages([{
      text: t('playground.ai_planning_needs'),
      duration: 3000
    }, {
      text: t('playground.ai_planning_places'),
      duration: 3000
    }, {
      text: t('playground.ai_planning_selecting'),
      duration: 3000
    }, {
      text: t('playground.ai_planning_descriptions'),
      duration: 3000
    }, {
      text: t('playground.ai_planning_sparkles'),
      duration: 3000
    }]);
    setShowAnimLoader(true);
    setSearchError(null);
    const startTime = Date.now();
    const minimumDisplayTime = 2000;
    const logAISuggestionToBackend = async (aiResponse: string) => {
      try {
        await logAISuggestion.mutateAsync({
          aiResponse,
          contextId: playgroundContext?.playgroundContextId
        });
      } catch (error) {
        logError('Failed to log AI suggestion:', error);
      }
    };
    let aiResponseToLog: string | null = null;
    try {
      // Always send the AI Travel Planner webhook English names - it's calibrated on English
      // place names regardless of the UI's display locale, and its response is matched back
      // against these same English names below.
      const availablePlaceNames = availablePlacesInMaps.map(place_0 => getEnglishPlaceName(englishPlaceNameByPlaceId, place_0));
      const response = await aiTravelPlanner.mutateAsync({
        chatInput: searchValue,
        availablePlaces: availablePlaceNames
      });
      const hasNoPlaces = !response?.PlacesResponse || response.PlacesResponse.length === 0;
      const hasNoMatches = response?.PlacesResponse?.some(place_1 => place_1.Name === 'No match found');
      const allPlacesUnavailable = response?.PlacesResponse && response.PlacesResponse.length > 0 && response.PlacesResponse.every(place_2 => !availablePlaceNames.includes(place_2.Name));
      const hasUnreachableSparkles = Boolean(response?.PlacesResponse?.some(place_3 => unselectablePlaces.some(unselectable => getEnglishPlaceName(englishPlaceNameByPlaceId, unselectable) === place_3.Name)));
      if ((hasNoPlaces || hasNoMatches || allPlacesUnavailable) && !hasUnreachableSparkles) {
        setShowNoResultsNotification(true);
        window.dispatchEvent(new CustomEvent('playground-map-zoom-to-default', {
          detail: {
            duration: 2000
          }
        }));
      }
      setAiTravelPlannerResponse(response);
      setSearchComplete(true);
      aiResponseToLog = JSON.stringify(response, null, 2);
    } catch (error_0) {
      logError('AI Travel Planner search failed:', error_0);
      setSearchError(t('content.feedback.search_error'));
      setShowNoResultsNotification(true);
      window.dispatchEvent(new CustomEvent('playground-map-zoom-to-default', {
        detail: {
          duration: 2000
        }
      }));
      aiResponseToLog = `Error: ${error_0 instanceof Error ? error_0.message : String(error_0)}`;
    } finally {
      try {
        if (aiResponseToLog) {
          await logAISuggestionToBackend(aiResponseToLog);
        }
      } catch (error) {
        logError('Failed to log AI suggestion in finally block:', error);
      }
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumDisplayTime - elapsedTime);
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      setIsSearching(false);
      setShowAnimLoader(false);
      setLoadingMessages([]);
    }
  };
  const handleSearchChange = (e_2: React.ChangeEvent<HTMLInputElement>) => {
    const value = e_2.target.value;
    setSearchValue(value);
    if (value !== searchValue) {
      setSearchError(null);
    }
    if (!value.trim()) {
      setAiTravelPlannerResponse(null);
    }
  };
  const handleSearchKeyDown = (e_3: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_3.key === 'Enter') {
      e_3.preventDefault();
      handleSearchSubmit();
    }
  };
  const triggerMapZoomOut = () => {
    window.dispatchEvent(new CustomEvent('playground-map-zoom-out', {
      detail: {
        zoom: 2.7,
        duration: 5000
      }
    }));
  };
  const triggerMapZoomToResults = () => {
    window.dispatchEvent(new CustomEvent('playground-map-zoom-to-results', {
      detail: {
        duration: 3000
      }
    }));
  };
  const scrollOut = async () => {
    const targetPosition = window.scrollY - window.innerHeight * 0.3;
    const tolerance = 40;
    const isAlreadyAtTarget = Math.abs(window.scrollY - targetPosition) <= tolerance;
    if (!isAlreadyAtTarget) {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      await new Promise(resolve => setTimeout(resolve, 350));
    }
  };

  // Zoom out the map when loading starts, zoom in when search completes
  useEffect(() => {
    if (isSearching && !searchComplete) {
      // Only zoom out when starting a new search (not when search is complete)
      // Small delay to ensure the loading modal is visible
      const timer = setTimeout(async () => {
        await scrollOut();
        triggerMapZoomOut();
        window.dispatchEvent(new CustomEvent('playground-ai-search-active', {
          detail: {
            active: true
          }
        }));
      }, 100);
      return () => clearTimeout(timer);
    } else if (searchComplete && !isSearching) {
      triggerMapZoomToResults();
    }
  }, [isSearching, searchComplete]);
  return <>
      {showNoResultsNotification && <Notification state="error" message={t('content.feedback.search_empty')} onClose={() => setShowNoResultsNotification(false)} variant="maps" />}

      {/* --- MOBILE --- */}
      <div className={clsx({
      [styles.base]: process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER !== 'true',
      [styles.mobile]: true
    })}>
        {process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER === 'true' ? <>
            <div className={styles.mobileSearchContainer}>
              <div className={clsx(styles.mobileSearchFieldContainer, showNoResultsNotification && styles.searchFieldError, searchValue && styles.hasValue)} onClick={handleContainerClick}>
                <div className={styles.sparkleIcon}>
                  <Sparkles decorative size="large" />
                </div>
                <input ref={searchInputRef} type="text" className={styles.mobileSearchField} placeholder="" aria-label={t('aria.destinations_search')} value={searchValue} onChange={handleSearchChange} onKeyDown={handleSearchKeyDown} />
                <div className={clsx(styles.placeholderContainer, styles.placeholderContainerWithSparkle)}>
                  <AnimatedPlaceholder />
                </div>
                {searchValue && <button type="button" className={styles.clearButton} onClick={clearSearchHandler} onMouseDown={handleClearButtonMouseDown} aria-label={t('aria.search_clear')}>
                    <Xmark size="small" decorative />
                  </button>}
                <button type="button" className={styles.textFieldIcon} onClick={handleSearchSubmit} onMouseDown={handleSearchButtonMouseDown} aria-label={t('aria.destinations_search')}>
                  <Search size="large" decorative color="var(--by-color-icon-contrast)" />
                </button>
              </div>
            </div>

            {/* Floating Pills */}
            <div className={styles.mobilePillsContainer}>
              {/* ORIGIN */}
              <div className={clsx(styles.mobilePill, styles.originPill)}>
                <OriginButtonMobile buttonVariant={BUTTON_VARIANT} />
              </div>

              {/* DATES */}
              <div className={styles.mobilePill}>
                <DatesButtonMobile buttonVariant={BUTTON_VARIANT} />
              </div>

              {/* DESTINATIONS */}
              <div className={styles.mobilePill}>
                <DestinationsButtonMobile buttonVariant="pill" maxLength={10} />
              </div>
            </div>
          </> : (/* Original non placefinder mobile layout */
      <Toolbar space="--by-space-200" ariaLabel={t('aria.requirements_edit')}>
            {/* ORIGIN */}
            <OriginButtonMobile buttonVariant={BUTTON_VARIANT} />

            {/* DATES */}
            <DatesButtonMobile buttonVariant={BUTTON_VARIANT} />

            {/* DESTINATIONS */}
            <DestinationsButtonMobile buttonVariant={BUTTON_VARIANT} maxLength={10} />
          </Toolbar>)}
      </div>

      {/* --- DESKTOP --- */}
      <div className={clsx(process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER === 'true' ? null : styles.base, styles.desktop)}>
        <div className={styles.toolbarReplacement} data-test-id="Toolbar">
          {/* ORIGIN */}
          <OriginButton buttonVariant={BUTTON_VARIANT} />

          {/* DATES */}
          <DatesButton buttonVariant={BUTTON_VARIANT} />

          {/* DESTINATIONS */}
          <DestinationsButton buttonVariant={BUTTON_VARIANT} />

          {process.env.NEXT_PUBLIC_AI_TRAVEL_PLANNER === 'true' ? <>
              <div className={styles.dividerWrapper}>
                <Divider variant="vertical" />
              </div>
              {/* AI travel planner */}
              <div className={clsx(styles.textFieldContainer, isSearchFocused && styles.textFieldContainerExpanded, showNoResultsNotification && styles.searchFieldError, searchValue && styles.hasValue)} onClick={handleContainerClick}>
                <div className={styles.sparkleIcon}>
                  <Sparkles decorative size="large" />
                </div>
                <input ref={searchInputRef} type="text" className={styles.textField} placeholder="" aria-label={t('aria.ai_planner_input')} value={searchValue} onChange={handleSearchChange} onFocus={handleSearchFocus} onBlur={handleSearchBlur} onKeyDown={handleSearchKeyDown} />
                {!searchValue && <div className={clsx(styles.placeholderContainer, styles.placeholderContainerWithSparkle)}>
                    <AnimatedPlaceholder />
                  </div>}
                {searchValue && <button type="button" className={styles.clearButton} onClick={clearSearchHandler} onMouseDown={handleClearButtonMouseDown} aria-label={t('aria.search_clear')}>
                    <Xmark size="small" decorative />
                  </button>}
                <button type="button" className={styles.textFieldIcon} onClick={handleSearchSubmit} onMouseDown={handleSearchButtonMouseDown} aria-label={t('aria.ai_planner_search')}>
                  <Search size="large" decorative color="var(--by-color-icon-contrast)" />
                </button>
              </div>
            </> : null}
        </div>

        {!isReview && <PlaygroundGenerateTripsButton />}
        {isReview && <div className={styles.ctaContainer}>
            <EditTripButton variant="small" ctaText={t('playground.emailCapture.cta')} onBeforeNavigate={topRightCtaOpensCapture ? handleReviewCtaBeforeNavigate : undefined} />
          </div>}
      </div>
    </>;
}
export { PrimaryEditBar };